import React, { useEffect, useRef } from 'react';
import { Candle, Overlay } from './types';

// =============================================================================
// CandleChart — a minimal, dependency-free candlestick chart on <canvas>.
// Features: pan (drag), zoom (wheel, anchored under cursor), auto-scaled price
// axis with "nice" ticks, time axis, crosshair with price readout, an OHLC +
// overlay legend, high-DPI (retina) rendering, and responsive resize.
// No third-party charting library, no external branding — this is CCFOS's own
// renderer, and the surface the ported indicators plug into.
// =============================================================================

interface Props {
  candles: Candle[];
  overlays?: Overlay[];
  height?: number;
}

const C = {
  bg: '#0a0f1a',
  grid: '#152136',
  text: '#8ea1bd',
  axis: '#1c2740',
  up: '#4ade80',
  down: '#f87171',
  cross: '#64769a',
  legendBg: 'rgba(9,14,24,0.88)',
};

const PRICE_AXIS_W = 64;
const TIME_AXIS_H = 24;
const PAD_TOP = 12;
const MIN_BARS = 15;

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const pad2 = (n: number) => (n < 10 ? '0' + n : '' + n);

// "Nice number" axis ticks (~count divisions) so labels land on round values.
function niceNum(range: number, round: boolean): number {
  const exp = Math.floor(Math.log10(range));
  const f = range / Math.pow(10, exp);
  let nf: number;
  if (round) nf = f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10;
  else nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nf * Math.pow(10, exp);
}
function niceTicks(min: number, max: number, count = 6): number[] {
  if (!(max > min)) return [min];
  const step = niceNum(niceNum(max - min, false) / (count - 1), true);
  const start = Math.floor(min / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= max + step * 0.5; v += step) ticks.push(v);
  return ticks;
}

export default function CandleChart({ candles, overlays = [], height = 460 }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Interaction state in refs (mutated live during pan/zoom → no React re-render).
  const view = useRef({ offset: 0, count: 120 }); // first visible index (may be fractional), # visible bars
  const mouse = useRef({ x: 0, y: 0, inside: false });
  const drag = useRef({ active: false, startX: 0, startOffset: 0 });
  const dims = useRef({ w: 0, h: height, dpr: 1 });
  const raf = useRef<number | null>(null);

  // Initialize the view to the most recent ~120 bars whenever the series changes.
  useEffect(() => {
    const count = clamp(120, MIN_BARS, Math.max(MIN_BARS, candles.length));
    view.current = { offset: Math.max(0, candles.length - count), count };
  }, [candles]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const requestDraw = () => {
      if (raf.current != null) return;
      raf.current = requestAnimationFrame(() => {
        raf.current = null;
        draw();
      });
    };

    function draw() {
      const ctx = canvas!.getContext('2d');
      if (!ctx) return;
      const { w, h, dpr } = dims.current;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = C.bg;
      ctx.fillRect(0, 0, w, h);

      const n = candles.length;
      if (n === 0) return;

      const plotW = w - PRICE_AXIS_W;
      const plotH = h - TIME_AXIS_H - PAD_TOP;
      const top = PAD_TOP;
      const { offset, count } = view.current;
      const barW = plotW / count;
      const first = clamp(Math.floor(offset), 0, n - 1);
      const last = clamp(Math.ceil(offset + count), 1, n);

      // Price range over visible candles + overlay values.
      let lo = Infinity;
      let hi = -Infinity;
      for (let i = first; i < last; i++) {
        if (candles[i].low < lo) lo = candles[i].low;
        if (candles[i].high > hi) hi = candles[i].high;
        for (const ov of overlays) {
          const v = ov.values[i];
          if (v != null && isFinite(v)) {
            if (v < lo) lo = v;
            if (v > hi) hi = v;
          }
        }
      }
      if (!isFinite(lo) || !isFinite(hi)) return;
      const vpad = (hi - lo) * 0.08 || 1;
      lo -= vpad;
      hi += vpad;

      const xOf = (i: number) => (i - offset) * barW + barW / 2;
      const yOf = (p: number) => top + ((hi - p) / (hi - lo)) * plotH;

      ctx.font = '11px system-ui, -apple-system, sans-serif';

      // Horizontal grid + price labels.
      ctx.textBaseline = 'middle';
      for (const t of niceTicks(lo, hi, 6)) {
        const y = yOf(t);
        if (y < top - 1 || y > top + plotH + 1) continue;
        ctx.strokeStyle = C.grid;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, Math.round(y) + 0.5);
        ctx.lineTo(plotW, Math.round(y) + 0.5);
        ctx.stroke();
        ctx.fillStyle = C.text;
        ctx.textAlign = 'left';
        ctx.fillText(t.toFixed(2), plotW + 6, y);
      }

      // Time labels (~6 across).
      ctx.textAlign = 'center';
      ctx.fillStyle = C.text;
      const every = Math.max(1, Math.floor(count / 6));
      for (let i = first; i < last; i++) {
        if (i % every !== 0) continue;
        const x = xOf(i);
        if (x < 2 || x > plotW - 2) continue;
        const d = new Date(candles[i].time);
        ctx.fillText(`${d.getUTCMonth() + 1}/${d.getUTCDate()}`, x, top + plotH + 12);
      }

      // Candles.
      for (let i = first; i < last; i++) {
        const c = candles[i];
        const x = xOf(i);
        const up = c.close >= c.open;
        const col = up ? C.up : C.down;
        ctx.strokeStyle = col;
        ctx.fillStyle = col;
        ctx.lineWidth = 1;
        const wx = Math.round(x) + 0.5;
        ctx.beginPath();
        ctx.moveTo(wx, yOf(c.high));
        ctx.lineTo(wx, yOf(c.low));
        ctx.stroke();
        const bw = Math.max(1, barW * 0.62);
        const yO = yOf(c.open);
        const yC = yOf(c.close);
        ctx.fillRect(x - bw / 2, Math.min(yO, yC), bw, Math.max(1, Math.abs(yC - yO)));
      }

      // Overlays (EMA lines, etc.).
      for (const ov of overlays) {
        ctx.strokeStyle = ov.color;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        let started = false;
        for (let i = first; i < last; i++) {
          const v = ov.values[i];
          if (v == null || !isFinite(v)) continue;
          const x = xOf(i);
          const y = yOf(v);
          if (!started) {
            ctx.moveTo(x, y);
            started = true;
          } else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      // Axis dividers.
      ctx.strokeStyle = C.axis;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(plotW + 0.5, 0);
      ctx.lineTo(plotW + 0.5, top + plotH);
      ctx.moveTo(0, top + plotH + 0.5);
      ctx.lineTo(plotW, top + plotH + 0.5);
      ctx.stroke();

      // Crosshair + cursor price label.
      const m = mouse.current;
      let hoveredIdx = last - 1;
      if (m.inside && m.x <= plotW) {
        hoveredIdx = clamp(Math.round(offset + (m.x - barW / 2) / barW), first, last - 1);
        const cx = xOf(hoveredIdx);
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = C.cross;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(Math.round(cx) + 0.5, top);
        ctx.lineTo(Math.round(cx) + 0.5, top + plotH);
        if (m.y >= top && m.y <= top + plotH) {
          ctx.moveTo(0, Math.round(m.y) + 0.5);
          ctx.lineTo(plotW, Math.round(m.y) + 0.5);
        }
        ctx.stroke();
        ctx.setLineDash([]);
        if (m.y >= top && m.y <= top + plotH) {
          const priceAtCursor = hi - ((m.y - top) / plotH) * (hi - lo);
          ctx.fillStyle = C.cross;
          ctx.fillRect(plotW, m.y - 9, PRICE_AXIS_W, 18);
          ctx.fillStyle = '#fff';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(priceAtCursor.toFixed(2), plotW + 6, m.y);
        }
      }

      // Legend (hovered candle, else last).
      drawLegend(ctx, candles[hoveredIdx], overlays, hoveredIdx);
    }

    function drawLegend(
      ctx: CanvasRenderingContext2D,
      c: Candle | undefined,
      ovs: Overlay[],
      idx: number
    ) {
      if (!c) return;
      const d = new Date(c.time);
      const chgCol = c.close >= c.open ? C.up : C.down;
      const rows: Array<{ t: string; c: string }> = [
        { t: `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`, c: C.text },
        {
          t: `O ${c.open.toFixed(2)}  H ${c.high.toFixed(2)}  L ${c.low.toFixed(2)}  C ${c.close.toFixed(2)}`,
          c: chgCol,
        },
      ];
      for (const ov of ovs) {
        const v = ov.values[idx];
        if (v != null && isFinite(v)) rows.push({ t: `${ov.label} ${v.toFixed(2)}`, c: ov.color });
      }
      ctx.font = '11px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      const padX = 8;
      const lh = 15;
      let boxW = 0;
      for (const r of rows) boxW = Math.max(boxW, ctx.measureText(r.t).width);
      boxW += padX * 2;
      const boxH = rows.length * lh + 10;
      ctx.fillStyle = C.legendBg;
      ctx.fillRect(8, 8, boxW, boxH);
      let y = 13;
      for (const r of rows) {
        ctx.fillStyle = r.c;
        ctx.fillText(r.t, 8 + padX, y);
        y += lh;
      }
    }

    // ---- Interaction ----
    const localXY = (e: MouseEvent) => {
      const r = canvas!.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };

    const onDown = (e: MouseEvent) => {
      const { x } = localXY(e);
      drag.current = { active: true, startX: x, startOffset: view.current.offset };
      canvas!.style.cursor = 'grabbing';
    };
    const onMove = (e: MouseEvent) => {
      const { x, y } = localXY(e);
      const { w, h } = dims.current;
      mouse.current = { x, y, inside: x >= 0 && x <= w && y >= 0 && y <= h };
      if (drag.current.active) {
        const plotW = w - PRICE_AXIS_W;
        const barW = plotW / view.current.count;
        const next = drag.current.startOffset - (x - drag.current.startX) / barW;
        view.current.offset = clamp(next, 0, Math.max(0, candles.length - view.current.count));
      }
      requestDraw();
    };
    const onUp = () => {
      drag.current.active = false;
      canvas!.style.cursor = 'crosshair';
    };
    const onLeave = () => {
      mouse.current.inside = false;
      requestDraw();
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const { x } = localXY(e);
      const plotW = dims.current.w - PRICE_AXIS_W;
      const frac = clamp(x / plotW, 0, 1);
      const { offset, count } = view.current;
      const anchor = offset + frac * count;
      const factor = e.deltaY > 0 ? 1.12 : 1 / 1.12; // down = zoom out (more bars)
      const newCount = clamp(count * factor, MIN_BARS, candles.length);
      const newOffset = clamp(anchor - frac * newCount, 0, Math.max(0, candles.length - newCount));
      view.current = { offset: newOffset, count: newCount };
      requestDraw();
    };

    const resize = () => {
      const rect = container!.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const cssW = Math.max(1, rect.width);
      canvas!.style.width = cssW + 'px';
      canvas!.style.height = height + 'px';
      canvas!.width = Math.round(cssW * dpr);
      canvas!.height = Math.round(height * dpr);
      dims.current = { w: cssW, h: height, dpr };
      draw(); // paint synchronously — first render must not depend on rAF
              // (hidden tabs pause rAF; interactions still throttle via requestDraw)
    };

    canvas.style.cursor = 'crosshair';
    canvas.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    canvas.addEventListener('mouseleave', onLeave);
    canvas.addEventListener('wheel', onWheel, { passive: false });

    const ro = new ResizeObserver(resize);
    ro.observe(container);
    resize();

    return () => {
      canvas.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('wheel', onWheel);
      ro.disconnect();
      if (raf.current != null) {
        cancelAnimationFrame(raf.current);
        raf.current = null; // reset so the next effect's requestDraw() isn't blocked
      }
    };
  }, [candles, overlays, height]);

  return (
    <div ref={containerRef} className="candlechart" style={{ height }}>
      <canvas ref={canvasRef} />
    </div>
  );
}
